import { Component } from '@theme/component';
import { isClickedOutside, setCookie } from '@theme/utilities';

/**
 * Dropdown localization component — trigger button + language panel with auto-submit.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button - The trigger button.
 * @property {HTMLDivElement} panel - The dropdown panel.
 * @property {HTMLInputElement} languageInput - The hidden language_code input.
 *
 * @extends {Component<Refs>}
 */
class DropdownLocalizationComponent extends Component {
  get isOpen() {
    return this.hasAttribute('data-open');
  }

  /**
   * Selects a language and submits the form.
   *
   * @param {string} isoCode - The language ISO code.
   * @param {Event} event - The event object.
   */
  selectLanguage(isoCode, event) {
    event.preventDefault();

    this.refs.languageInput.value = isoCode;
    this.querySelector('form')?.submit();
  }

  /** Toggles the panel open/closed. */
  toggleSelector() {
    return this.isOpen ? this.hidePanel() : this.showPanel();
  }

  /** Shows the panel. */
  showPanel() {
    if (this.isOpen) return;

    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener('click', this.#handleClickOutside);

    this.setAttribute('data-open', '');
    this.refs.button.setAttribute('aria-expanded', 'true');
  }

  /** Hides the panel. */
  hidePanel = () => {
    if (!this.isOpen) return;

    this.removeEventListener('keyup', this.#handleKeyUp);
    document.removeEventListener('click', this.#handleClickOutside);

    this.removeAttribute('data-open');
    this.refs.button?.setAttribute('aria-expanded', 'false');
  };

  /**
   * @param {PointerEvent} event
   */
  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) {
      this.hidePanel();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyUp = (event) => {
    if (event.key === 'Escape') {
      this.hidePanel();
      event.stopPropagation();
      this.refs.button?.focus();
    }
  };
}

/**
 * Drawer localization component — resets the form when the dialog closes.
 * Listens for `dialog:close` which fires after the closing animation completes.
 *
 * @extends {Component}
 */
class DrawerLocalizationComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#dialogComponent = this.querySelector('dialog-component');
    this.#dialogComponent?.addEventListener('dialog:close', this.#onDialogClose);
    this.#defaultChecked = Array.from(this.querySelectorAll('input[type="radio"]:checked'));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#dialogComponent?.removeEventListener('dialog:close', this.#onDialogClose);
    this.#dialogComponent = null;
    this.#defaultChecked = [];
  }

  /** @type {HTMLElement | null} */
  #dialogComponent = null;

  /** @type {HTMLInputElement[]} - The radios Liquid rendered as checked. */
  #defaultChecked = [];

  // The radios sit outside the forms, so form.reset() would not reach them.
  #onDialogClose = () => {
    this.#defaultChecked.forEach((input) => {
      input.checked = true;
    });
  };

  /**
   * Submits a localization form, returning the buyer to the page they were on.
   *
   * @param {string} formId - Id of the form to submit.
   * @param {string} inputName - Name of the hidden field carrying the value.
   * @param {string} value - The iso code to submit.
   */
  #submitLocalization(formId, inputName, value) {
    const form = /** @type {HTMLFormElement | null} */ (this.querySelector(`#${formId}`));
    const input = /** @type {HTMLInputElement | null} */ (
      form?.querySelector(`input[name="${inputName}"]`) ?? null
    );

    if (!form || !input) return false;

    input.value = value;

    // Without this Shopify falls back to Referer, which strict referrer policies drop.
    let returnTo = /** @type {HTMLInputElement | null} */ (
      form.querySelector('input[name="return_to"]')
    );
    if (!returnTo) {
      returnTo = document.createElement('input');
      returnTo.type = 'hidden';
      returnTo.name = 'return_to';
      form.appendChild(returnTo);
    }
    returnTo.value = window.location.pathname + window.location.search;

    form.submit();
    return true;
  }

  handleChangeLocation = () => {
    const countrySelected = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="localization_country"]:checked')
    );
    const languageSelected = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="localization_language"]:checked')
    );

    if (!countrySelected?.value) {
      return;
    }

    setCookie('geolocationModal', 'true', 1);

    const countryCode = countrySelected.dataset.country;
    const markets = (this.dataset.markets ?? '').split(',').filter(Boolean);

    // Not one of our markets, so it lives on a separate storefront.
    if (!countryCode || !markets.includes(countryCode)) {
      window.location.assign(countrySelected.value);
      return;
    }

    if (countryCode !== this.dataset.currentCountry) {
      if (this.#submitLocalization('DrawerCountryForm', 'country_code', countryCode)) {
        return;
      }

      window.location.assign(countrySelected.value);
      return;
    }

    // Country unchanged — apply the language choice instead.
    if (languageSelected && languageSelected.value !== this.dataset.currentLanguage) {
      this.#submitLocalization('DrawerLanguageForm', 'language_code', languageSelected.value);
    }
  }
}

if (!customElements.get('dropdown-localization-component')) {
  customElements.define('dropdown-localization-component', DropdownLocalizationComponent);
}

if (!customElements.get('drawer-localization-component')) {
  customElements.define('drawer-localization-component', DrawerLocalizationComponent);
}
